import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiSave, FiX } from 'react-icons/fi';
import integrationService from '../../services/integrationService';

const IntegrationCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    integration_type: 'api',
    provider: '',
    description: '',
    status: 'pending',
    endpoint_url: '',
    auth_method: 'none',
    sync_frequency: 'manual',
    is_enabled: true,
    configuration: {},
    credentials: {},
    metadata: {}
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleJsonChange = (field, value) => {
    try {
      const parsed = value ? JSON.parse(value) : {};
      setFormData(prev => ({
        ...prev,
        [field]: parsed
      }));
    } catch (error) {
      // Invalid JSON, keep as is
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await integrationService.createIntegration(formData);
      toast.success('Integration created successfully');
      navigate('/app/integrations');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create integration');
      console.error('Error creating integration:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Create New Integration</h1>
        <p className="mt-1 text-sm text-gray-500">
          Set up a new third-party integration
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 custon-input"
                placeholder="e.g., Slack Integration"
                style={{'border':'1px solid #dee2e6','padding':'12px'}}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Integration Type *
              </label>
              <select
                name="integration_type"
                value={formData.integration_type}
                onChange={handleChange}
                required
                style={{'border':'1px solid #dee2e6','padding':'12px'}}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="api">API</option>
                <option value="webhook">Webhook</option>
                <option value="oauth">OAuth</option>
                <option value="database">Database</option>
                <option value="file_sync">File Sync</option>
                <option value="messaging">Messaging</option>
                <option value="calendar">Calendar</option>
                <option value="crm">CRM</option>
                <option value="accounting">Accounting</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Provider
              </label>
              <input
                type="text"
                name="provider"
                value={formData.provider}
                onChange={handleChange}
                style={{'border':'1px solid #dee2e6','padding':'12px'}}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="e.g., Slack, Jira, GitHub"
              />
            </div>

            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={3}
                style={{'border':'1px solid #dee2e6','padding':'12px'}}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Describe the purpose of this integration"
              />
            </div>
          </div>
        </div>

        {/* Connection Settings */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Connection Settings</h2>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Endpoint URL
              </label>
              <input
                type="url"
                name="endpoint_url"
                style={{'border':'1px solid #dee2e6','padding':'12px'}}
                value={formData.endpoint_url}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="https://api.example.com/v1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Authentication Method
              </label>
              <select
                name="auth_method"
                value={formData.auth_method}
                onChange={handleChange}
                style={{'border':'1px solid #dee2e6','padding':'12px'}}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="none">None</option>
                <option value="api_key">API Key</option>
                <option value="oauth2">OAuth 2.0</option>
                <option value="basic">Basic Auth</option>
                <option value="bearer_token">Bearer Token</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Sync Frequency
              </label>
              <select
                name="sync_frequency"
                value={formData.sync_frequency}
                onChange={handleChange}
                style={{'border':'1px solid #dee2e6','padding':'12px'}}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="realtime">Real-time</option>
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="manual">Manual</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                style={{'border':'1px solid #dee2e6','padding':'12px'}}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="pending">Pending</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="error">Error</option>
              </select>
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                name="is_enabled"
                checked={formData.is_enabled}
                onChange={handleChange}
                style={{'border':'1px solid #dee2e6' ,'padding':'12px'}}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-gray-900">
                Enable integration
              </label>
            </div>
          </div>
        </div>

        {/* Advanced Configuration */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Advanced Configuration</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Configuration (JSON)
              </label>
              <textarea
                rows={4}
                style={{'border':'1px solid #dee2e6','padding':'12px'}}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 font-mono text-sm"
                placeholder='{"key": "value"}'
                onChange={(e) => handleJsonChange('configuration', e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter integration-specific configuration as JSON
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Credentials (JSON)
              </label>
              <textarea
                rows={4}
                style={{'border':'1px solid #dee2e6','padding':'12px'}}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 font-mono text-sm"
                placeholder='{"api_key": "your-key-here"}'
                onChange={(e) => handleJsonChange('credentials', e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter authentication credentials as JSON (will be encrypted)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Metadata (JSON)
              </label>
              <textarea
              style={{'border':'1px solid #dee2e6','padding':'12px'}}
                rows={4}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 font-mono text-sm"
                placeholder='{"custom_field": "value"}'
                onChange={(e) => handleJsonChange('metadata', e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500">
                Enter additional metadata as JSON
              </p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/app/integrations')}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            <FiX className="mr-2" />
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            <FiSave className="mr-2" />
            {loading ? 'Creating...' : 'Create Integration'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default IntegrationCreate;

